import YberApi from '@api/YberApi';
import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import DisturbanceMapApi from '@api/DisturbanceMapApi';
import RootStore from '@stores/RootStore';
import SsoStore from '@stores/SsoStore';
import { YberStatus } from '@config/YberConfig';
import {
  DisturbanceLocalNotifications,
  DisturbanceMap,
  DisturbanceMapCounty,
  DisturbanceNotice,
  DisturbanceNotifications,
  DisturbanceRegion
} from '@config/DisturbanceConfig';
import { getAuthType } from '@util/ApiUtil';

export default class DisturbanceMapStore {
  @observable accessor yber: YberStatus = undefined;
  @observable accessor notices: DisturbanceNotifications = undefined;
  @observable accessor map: DisturbanceMap = undefined;
  @observable accessor isLoading: boolean = true;
  @observable accessor loadingError: boolean = false;
  @observable accessor isLoadingNotices: boolean = true;
  @observable accessor loadingErrorNotices: boolean = false;
  @observable accessor modalIsOpen: boolean = false;
  @observable accessor modalScrollId: string = null;

  private readonly ssoStore: SsoStore;

  public constructor(rootStore: RootStore) {
    makeObservable(this);
    this.ssoStore = rootStore.ssoStore;
  }

  @action
  public async fetchMap(): Promise<void> {
    try {
      this.resetState();
      this.map = await DisturbanceMapApi.getMap(getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
    } catch (err) {
      console.error(err);
      this.loadingError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  public async fetchNotices(): Promise<void> {
    try {
      this.resetNotices();
      this.notices = await DisturbanceMapApi.getNotices(getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
    } catch (err) {
      console.error(err);
      this.loadingErrorNotices = true;
    } finally {
      this.isLoadingNotices = false;
    }
  }

  @action
  public async fetchYber(): Promise<void> {
    try {
      this.yber = await YberApi.getStatus();
    } catch (err) {
      console.error(err);
    }
  }

  @computed
  public get activeCountiesList(): DisturbanceRegion[] {
    const disturbanceCounties = this.map !== undefined && 'disturbanceCounties' in this.map ? this.map.disturbanceCounties : [];
    const counties: DisturbanceRegion[] = Object.values(DisturbanceRegion);
    const countyNames: string[] = Object.keys(DisturbanceRegion);
    const activeCountySet: Set<DisturbanceRegion> = new Set();

    disturbanceCounties.forEach((county: DisturbanceMapCounty) => {
      if (counties.includes(county.countyKey)) {
        activeCountySet.add(county.countyKey);
      }
    });
    this.localNotices.forEach((localNotice: DisturbanceLocalNotifications) => {
      if (countyNames.includes(localNotice.countyName)) {
        activeCountySet.add(counties.find((county: DisturbanceRegion) => county.valueOf() === localNotice.countyName));
      }
    });
    return Array.from(activeCountySet);
  }

  @computed
  public get globalNotices(): DisturbanceNotice[] {
    return this.notices !== undefined && 'globalNotices' in this.notices ? this.notices.globalNotices : [];
  }

  @computed
  public get localNotices(): DisturbanceLocalNotifications[] {
    const localNotices: DisturbanceLocalNotifications[] = this.notices !== undefined && 'localNotices' in this.notices ? this.notices.localNotices : [];
    return localNotices;
  }

  public openModal(scrollToId: string = null) {
    runInAction(() => {
      this.modalIsOpen = true;
      this.modalScrollId = scrollToId;
    })
  }

  public closeModal() {
    runInAction(() => {
      this.modalIsOpen = false;
      if (this.modalScrollId) {
        this.modalScrollId = null;
      }
    })
  }

  private resetState = (): void => {
    this.map = undefined;
    this.isLoading = true;
    this.loadingError = false;
  };

  private resetNotices = (): void => {
    this.isLoadingNotices = true;
    this.loadingErrorNotices = false;
  };
}
